import * as React from "react";

import { cn } from "@/lib/utils";
import { pageInfoTarget, type PageInfoTargetKey } from "@/components/page-info/pageInfoTarget";

export interface UnderlineTabOption<T extends string> {
  value: T;
  label: string;
  /** Optional leading icon or glyph shown before the label. */
  icon?: React.ReactNode;
  /** Optional trailing count badge. Omit for tab strips without counts. */
  count?: number;
  /** Badge tone. "alert" paints a non-zero count red whichever tab is active
   *  (the Overdue tab, where the count is a warning, not a selection cue). */
  countTone?: "default" | "alert";
  /** Shown but not clickable (e.g. a filter option with nothing in it). */
  disabled?: boolean;
}

interface UnderlineTabsProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: UnderlineTabOption<T>[];
  className?: string;
  /** Lets the page's ⓘ dialog point at this strip ("Show me"). */
  infoTarget?: PageInfoTargetKey;
}

/**
 * UnderlineTabs — the app's standard tab strip: options sit on a rule, the
 * active one marked with a 2px brand-red underline. Extracted from the Issues
 * page so the Documents view switcher shares one implementation (Issues passes
 * `count`, Documents passes `icon` and no counts).
 *
 * The rule under the tabs is the HOST's own bottom border, not the strip's:
 * every host is a `border-b` header block and passes `className="-mb-px"`, so
 * the strip's last pixel row lands on that border and the active tab's
 * underline paints over it. The strip deliberately draws no border of its own
 * — a strip that both bordered itself AND pulled its buttons up by 1px
 * overflowed its own scroll box by that pixel, and since `overflow-x: auto`
 * forces `overflow-y` to compute to `auto` too, Chrome rendered a permanent
 * 1px-tall vertical scrollbar under the tabs (reported 2026-07-29).
 */
export function UnderlineTabs<T extends string>({
  value,
  onValueChange,
  options,
  className,
  infoTarget,
}: UnderlineTabsProps<T>) {
  return (
    <div
      className={cn("flex overflow-x-auto", className)}
      {...(infoTarget ? pageInfoTarget(infoTarget) : {})}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isActive}
            disabled={option.disabled}
            onClick={() => onValueChange(option.value)}
            className={cn(
              "flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors [&_svg]:size-4 [&_svg]:shrink-0",
              isActive
                ? "border-primary font-medium text-primary"
                : "border-transparent text-muted-foreground",
              option.disabled
                ? "cursor-not-allowed opacity-50"
                : !isActive && "hover:border-border hover:text-foreground"
            )}
          >
            {option.icon != null && (
              <span aria-hidden="true" className="inline-flex items-center">
                {option.icon}
              </span>
            )}
            <span>{option.label}</span>
            {option.count != null && (
              <span
                className={cn(
                  "inline-flex h-[16px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold",
                  option.countTone === "alert"
                    ? option.count > 0
                      ? "bg-[#fcebeb] text-[#ef4129]"
                      : "bg-muted text-muted-foreground"
                    : isActive
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
